import React, { useEffect, useState } from "react";
import { HeaderDesktop, HeaderMobile } from "./layout/appHeader/index";
import NavBar from "./layout/appNav/VerticalNavWrapper";
import {
    Login
} from "./pages/index";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import "./app.css";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import classNames from "classnames";
import { withSnackbar } from "./utils/SnackbarHOC";
import useOnlineStatus from "./utils/useOnlineStatus ";
import { Box, CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import { theme } from "./utils/Theme";
import ResponsiveLayout from "./layout/LayoutReponsive/LayoutResponsive";
import PrivatesRoutes from "./routes";
import { Provider } from "react-redux";
import store from "./redux/store/store";
import SwipeableDrawerSummary from "./components/SwipeableDrawerSummary";
import MailingModal from "./components/MailingModal/MailingModal";
import ChatbotIA from "./components/ChatbotIA";
import { getUserData } from "./utils/FunctionHelpers";
import NotificationSound from "./assets/sounds/notification-sound.mp3";
import { getUserNotifications } from "./services/NotificationService";
import { FeatureFlagsProvider } from "./providers/FeatureFlagsProvider";
import CollaborateurPublic from "./pages/Collaborateur/Public/CollaborateurPublic";
import { CustomToastContainer, notifyError, notifySuccess } from "./components/CustomToast";
import ForgotPassword from "./pages/login/ForgotPassword";
import ResetPassword from "./pages/login/ResetPassword";

function App({ snackbarShowMessage }) {
    const [sideBarCollapsed, setSideBarCollapsed] = useState(false);
    const isOnline = useOnlineStatus();
    const [notifications, setNotifications] = useState([]);
    const isAuthenticated = () => !!getUserData();

    const isMdMode = useMediaQuery(theme.breakpoints.down('md'));
    const isSmMode = useMediaQuery(theme.breakpoints.down('sm'));

    const playNotificationSound = () => {
        const audio = new Audio(NotificationSound);
        audio.play();
    };

    const loadNotifications = async () => {
        try {
            const { data } = await getUserNotifications();
            setNotifications(data);
            if (data.filter((notification) => !notification.seen).length > 0) {
                snackbarShowMessage(
                    "Vous avez des nouvelles notifications !",
                    "info",
                    2000,
                    "bottom",
                    "left"
                );
            }
        } catch (error) {
            console.error("Impossible de charger les notifications.", error);
            setNotifications([]);
        }
    };

    const [reloadNotifications, setReloadNotifications] = useState(false);

    useEffect(() => {
        if (getUserData()) {
            loadNotifications();
            const notificationHubUrl = `${process.env.NODE_ENV === 'production' ? window.location.origin : "http://localhost:4000"}/.well-known/mercure`
            const url = new URL(notificationHubUrl);
            url.searchParams.append("topic", `user/${getUserData()?.uuid}`);

            const eventSource = new EventSource(url);

            eventSource.onmessage = (event) => {
                const data = JSON.parse(event.data);
                setNotifications((prev) => [data.data, ...prev]);
                snackbarShowMessage(
                    data?.data?.title || "Une nouvelle tâche vous a été assignée",
                    "info",
                    2000,
                    "bottom",
                    "left"
                );
                playNotificationSound();
            };

            return () => {
                eventSource.close();
            };
        }
    }, [getUserData()?.uuid]);

    const handleClick = () => {
        setSideBarCollapsed(!sideBarCollapsed);
    };

    const navBarProps = {
        sideBarCollapsed: sideBarCollapsed,
    };

    const renderAuthenticatedRoutes = () => (
        <FeatureFlagsProvider>
            <div
                className={classNames({
                    main_content: true,
                    dashboard_part: true,
                })}
            >
                <Box className="box">
                    <Box className="box_container">
                        {!isMdMode &&
                            <Box className="box_sideBar">
                                <NavBar
                                    {...navBarProps}
                                    reloadNotifications={reloadNotifications}
                                    onClick={handleClick}
                                />
                            </Box>}
                        <Box className="box_main">
                            <Box className="box_header " sx={isSmMode ? { marginBottom: "4rem" } : {}}>
                                {isMdMode ?
                                    <HeaderMobile handleClick={handleClick} notifications={notifications} setNotifications={setNotifications} /> :
                                    <HeaderDesktop
                                        notifications={notifications}
                                        setNotifications={setNotifications}
                                        handleClick={handleClick}
                                    />
                                }
                            </Box>
                            <ResponsiveLayout>
                                <PrivatesRoutes
                                    snackbarShowMessage={snackbarShowMessage}
                                    reloadNotifications={reloadNotifications}
                                    setReloadNotifications={setReloadNotifications}
                                />
                            </ResponsiveLayout>
                        </Box>
                    </Box>
                </Box>
                <MailingModal />
                <SwipeableDrawerSummary />
                <ChatbotIA />
            </div>
        </FeatureFlagsProvider>
    );

    return (
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <BrowserRouter>
                        <Switch>
                            <Route path="/login">
                                <Login snackbarShowMessage={snackbarShowMessage} />
                            </Route>
                            <Route exact path="/forgot-password">
                                <ForgotPassword snackbarShowMessage={snackbarShowMessage} />
                            </Route>
                            <Route exact path="/reset-password">
                                <ResetPassword snackbarShowMessage={snackbarShowMessage} />
                            </Route>
                            <Route path="/collaborateur/:id">
                                <CollaborateurPublic />
                            </Route>

                            <Route>
                                {isAuthenticated() ? (
                                    renderAuthenticatedRoutes()
                                ) : (
                                    <Login snackbarShowMessage={snackbarShowMessage} />
                                )}
                            </Route>
                        </Switch>
                    </BrowserRouter>
                    <CustomToastContainer />
                </LocalizationProvider>
            </ThemeProvider>
        </Provider>
    );
}

export default withSnackbar(App);
